
/* ----------------------- Series Card ----------------------- */
import {SeriesDTO} from "@/generated-client";
import React from "react";
import {Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle} from "@/components/ui/card";
import {Localizer} from "@/main/react/Types";
import DateFormatter from "@/main/common/DateFormatter";
import SeriesButtons from "@/main/react/series/SeriesButtons";
import {type AnalyticsSurface} from "@/main/analytics/analyticsEvents";
import {CardMetadataBadges} from "@/main/react/card/CardMetadataBadges";
import {CalendarDays, Loader2, Lock, MessageSquareText, Users} from "lucide-react";
import {getDisplayUsername, getUserProfilePath} from "@/main/common/UsernameDisplayUtils";
import SensitiveBadge from "@/main/react/sensitive/SensitiveBadge";
import SensitiveMediaGuard from "@/main/react/sensitive/SensitiveMediaGuard";
import { getSensitivePreferenceMode, resolveSensitiveMaskingConfig, type SensitiveMaskingDirective } from "@/main/common/SensitivePreference";
import {
    getUserFacingSeriesDescription,
    getUserFacingSeriesTitle,
    isBookmarksSeries,
} from "@/main/react/series/BookmarksSeriesLocalization";
import {SMALL_IMAGE_SIZE} from "@/main/util/ImageUrlHelper";
import { getFriendlyCountBadgeLabel, getFriendlyPrivateBadgeLabel } from "@/main/react/card/CardMetadataBadgeCopy";
import { MetaHandler } from "@/main/common/MetaHandler";
import { onDiscussionAddedToSeries, onDiscussionRemovedFromSeries, onSeriesFollowAdded, onSeriesFollowRemoved } from "@/main/common/EventRegistry";
import {navigateToUrl} from "@/main/common/BrowserNavigation";
import { getMaskedContentWarningCopy } from "@/main/react/sensitive/ContentWarningCopy";
import type { ImageCarouselRenderabilityState } from "@/main/react/image/ImageCarouselDisplayer";
import DiscussionCardMediaCarousel, { type DiscussionCardMediaItem } from "@/main/react/card/DiscussionCardMediaCarousel";
import { buildSeriesCardMediaItems } from "@/main/react/series/SeriesImageUtils";
import { FEED_CARD_MEDIA_BOX_CLASS, FEED_CARD_MEDIA_SLIDE_CLASS } from "@/main/react/card/feedCardMediaBox";
import AuthorAttribution from "@/main/react/component/AuthorAttribution";

type SeriesWithMedia = SeriesDTO & {
    imageUrls?: string | string[] | null;
    cardMediaItems?: string | null;
    sensitive?: boolean | null;
};

function createInitialSeriesMediaRenderabilityState(items: readonly DiscussionCardMediaItem[]): ImageCarouselRenderabilityState | null {
    if (items.length === 0) {
        return null;
    }

    const loadedImages = items.filter((item) => item.kind === "iframe").length;

    return {
        totalImages: items.length,
        loadedImages,
        failedImages: 0,
        hasRenderableImages: loadedImages > 0,
        allImagesFailed: false,
        isResolving: loadedImages === 0,
    };
}

export function SeriesCardDisplayer(props: {
    series: SeriesDTO;
    sensitiveMasking?: SensitiveMaskingDirective;
    /** Where this card is rendered, forwarded to SeriesButtons for follow/unfollow attribution. */
    surface?: AnalyticsSurface;
    localizer: Localizer;
    /** Extra classes merged into the card root (e.g. `h-full` for equal-height carousel rows). */
    className?: string;
    /**
     * `false` drops the owner byline. Set by the owner's own series list, where every card repeats
     * the same avatar and handle the route already names. One contract with
     * DiscussionCardDisplayer and ThoughtCardDisplayer — see the note on the former. Design doc §9.3.
     *
     * Unlike the discussion card, this byline carries no timestamp, so hiding it removes the row
     * outright rather than leaving a detail line behind.
     */
    showAuthor?: boolean;
    /**
     * `false` withholds the "No description" placeholder, leaving the description line out entirely
     * when the series has none (ux-critique `LANDING-69`). Set by the marketing showcase: the
     * placeholder is app-owned copy naming an absence, which earns its keep on an owner-facing list
     * (it names something to fix) and costs a card's most persuasive line on an acquisition page,
     * where nobody can act on it. Defaults to `true`, so every existing caller is unchanged.
     */
    showDescriptionPlaceholder?: boolean;
}) {
    const {
        series,
        sensitiveMasking,
        surface = "series_card",
        localizer,
        className,
        showAuthor = true,
        showDescriptionPlaceholder = true,
    } = props;
    const [displayedSeries, setDisplayedSeries] = React.useState<SeriesWithMedia>(series as SeriesWithMedia);
    const seriesWithMedia = displayedSeries as SeriesWithMedia;
    const createdAt = (series)?.createdAt;
    const df = new DateFormatter(localizer);
    const displayOwnerUsername = getDisplayUsername(displayedSeries.ownerUsername, localizer);
    const ownerProfilePath = getUserProfilePath(displayedSeries.ownerUsername, "discussions");
    const displayTitle = getUserFacingSeriesTitle(seriesWithMedia as any, localizer);
    const displayDescription = getUserFacingSeriesDescription(seriesWithMedia as any, localizer);
    const descriptionText = displayDescription || (showDescriptionPlaceholder ? localizer("series.no_description") : "");
    // ux-critique EMPTY-STATES-48. Structural widening rather than a generated-client type, in the same
    // shape this file already uses for `mainLocale`: the field is new on the wire and the client is
    // regenerated from a running backend, which a fan-out batch does not have. The array is present only
    // on rows the server resolved as viewer-owned, so an absent field is the normal case, not an error.
    const recentTitles = ((series as SeriesDTO & { recentDiscussionTitles?: string[] | null }).recentDiscussionTitles ?? [])
        .filter((title): title is string => typeof title === "string" && title.trim().length > 0);
    // ux-critique EMPTY-STATES-84 — the zero branch of the contents preview, and the predicate is the
    // whole difficulty. `recentDiscussionTitles` CANNOT carry it: the repository attaches it with a
    // plain map lookup (`UserCollectionPaginationRepository.attachOwnerContentsPreview`), so an owned
    // series with no posts is set to null, byte-identical on the wire to every row that never had the
    // preview computed at all — which is every card from SeriesRepositoryImpl and the home rails,
    // including a viewer-OWNED one with plenty of posts. Gating on "owner and no titles" would state
    // "no posts yet" on an owner's full series in search results.
    //
    // The post count is the signal that holds: it is present wherever `viewer_owner` is selected, it is
    // the same field the "0 posts" chip already renders, and a series with zero posts has an empty
    // contents block on every surface by definition. A count that is genuinely absent drops the branch
    // rather than guessing.
    const isOwnedEmptySeries = Boolean(displayedSeries.isViewerOwner) && displayedSeries.discussionsCount === 0;
    const createdAtLabel = createdAt ? df.timeSince(createdAt) : "";
    const seriesMediaItems = React.useMemo(
        () => buildSeriesCardMediaItems({
            thumbnailUrl: displayedSeries.thumbnailUrl,
            imageUrls: seriesWithMedia.imageUrls,
            cardMediaItems: seriesWithMedia.cardMediaItems,
        }),
        [displayedSeries.thumbnailUrl, seriesWithMedia.cardMediaItems, seriesWithMedia.imageUrls],
    );
    const [imageRenderability, setImageRenderability] = React.useState<ImageCarouselRenderabilityState | null>(null);
    const initialMediaRenderabilityState = React.useMemo(
        () => createInitialSeriesMediaRenderabilityState(seriesMediaItems),
        [seriesMediaItems],
    );
    const isSensitive = Boolean(seriesWithMedia.sensitive);
    const { sensitivePreference, hideSensitiveMedia } = resolveSensitiveMaskingConfig(getSensitivePreferenceMode(), sensitiveMasking);
    const warningCopy = React.useMemo(
        () => getMaskedContentWarningCopy(localizer, { isSensitive }),
        [isSensitive, localizer],
    );
    const bookmarksCollection = isBookmarksSeries(seriesWithMedia as any);
    const privateVisibilityLabel = getFriendlyPrivateBadgeLabel(localizer, "badge.series.private", displayedSeries.privateAccess);
    const postCountLabel = getFriendlyCountBadgeLabel(localizer, "badge.series.discussions", displayedSeries.discussionsCount, { showZero: true });
    const loggedInUsername = MetaHandler.getLoggedInUsername();
    // Prefer the slug, fall back to the public id, and treat a fully-missing target as no link — a
    // raw `displayedSeries.slug` yields `/series/undefined` when the slug is null. Matches the
    // slug-or-publicId path target used by the home rails and other cards.
    const seriesPathTarget = displayedSeries.slug?.trim() || displayedSeries.publicId?.trim();
    const seriesHref = seriesPathTarget ? `/series/${encodeURIComponent(seriesPathTarget)}` : null;

    React.useEffect(() => {
        setDisplayedSeries(series as SeriesWithMedia);
    }, [series]);

    React.useEffect(() => {
        setImageRenderability(initialMediaRenderabilityState);
    }, [initialMediaRenderabilityState]);

    React.useEffect(() => {
        const seriesPublicId = displayedSeries.publicId?.trim();
        if (!seriesPublicId) {
            return;
        }

        const applyDiscussionSeriesUpdate = (payloadSeries: SeriesDTO | null | undefined, fallbackDelta: number) => {
            setDisplayedSeries((previous) => ({
                ...previous,
                ...(payloadSeries ? payloadSeries as SeriesWithMedia : {}),
                discussionsCount: payloadSeries?.discussionsCount ?? Math.max(0, Number(previous.discussionsCount ?? 0) + fallbackDelta),
                imageUrls: (payloadSeries as SeriesWithMedia | null | undefined)?.imageUrls ?? previous.imageUrls,
                cardMediaItems: (payloadSeries as SeriesWithMedia | null | undefined)?.cardMediaItems ?? previous.cardMediaItems,
            }));
        };

        const unsubscribeDiscussionAdded = onDiscussionAddedToSeries((payload) => {
            if (!payload || payload.seriesPublicId !== seriesPublicId) {
                return;
            }

            applyDiscussionSeriesUpdate((payload as typeof payload & { series?: SeriesDTO }).series, 1);
        });

        const unsubscribeDiscussionRemoved = onDiscussionRemovedFromSeries((payload) => {
            if (!payload || payload.seriesPublicId !== seriesPublicId) {
                return;
            }

            applyDiscussionSeriesUpdate((payload as typeof payload & { series?: SeriesDTO }).series, -1);
        });

        const unsubscribeAdded = onSeriesFollowAdded((payload) => {
            if (!payload || payload.seriesPublicId !== seriesPublicId) {
                return;
            }

            setDisplayedSeries((previous) => ({
                ...previous,
                followersCount: (previous.followersCount ?? 0) + 1,
                isViewerFollowing: loggedInUsername && payload.followerUsername === loggedInUsername ? true : previous.isViewerFollowing,
            }));
        });

        const unsubscribeRemoved = onSeriesFollowRemoved((payload) => {
            if (!payload || payload.seriesPublicId !== seriesPublicId) {
                return;
            }

            setDisplayedSeries((previous) => ({
                ...previous,
                followersCount: Math.max(0, (previous.followersCount ?? 0) - 1),
                isViewerFollowing: loggedInUsername && payload.followerUsername === loggedInUsername ? false : previous.isViewerFollowing,
            }));
        });

        return () => {
            unsubscribeDiscussionAdded();
            unsubscribeDiscussionRemoved();
            unsubscribeAdded();
            unsubscribeRemoved();
        };
    }, [displayedSeries.publicId, loggedInUsername]);

    React.useEffect(() => {
        if (seriesMediaItems.length === 0) {
            setImageRenderability(null);
            return;
        }

        setImageRenderability(initialMediaRenderabilityState);
    }, [initialMediaRenderabilityState, seriesMediaItems.length]);

    const shouldAttemptCarousel = seriesMediaItems.length > 0;
    const allImagesFailed = shouldAttemptCarousel && Boolean(imageRenderability?.allImagesFailed);
    const showLoadingSpinner = shouldAttemptCarousel
        && !allImagesFailed
        && !imageRenderability?.hasRenderableImages;
    const showCarousel = shouldAttemptCarousel && !allImagesFailed;

    return (
        <Card
            /* No resting or `focus-within` shadow — same dead-token cleanup as `FEED_CARD_ROOT_CLASS`,
               whose comment carries the reasoning. `--surface-shadow-soft` is `none` in both themes, so
               this is a no-op visually; elevation is the hairline border (flat-X rule 1). */
            className={`relative w-full max-w-full min-h-0 overflow-hidden rounded-xl border border-border py-0 gap-0 card-hover ${className ?? ""}`}
        >
                {/* The guard covers the MEDIA WELL and nothing else. It used to open before the
                    CardHeader and close after the CardFooter, so the title, the description, the date
                    badge and the entire owner action row sat inside the scrim: blurred to illegibility,
                    `pointer-events-none` and `aria-hidden`. On `/my_dashboard/series` that meant the one
                    series a member marked sensitive was the one row they could neither identify nor act
                    on, on the surface whose whole job is managing their own work (`DASHBOARD-11`).

                    The scope is `series.settings.sensitive_hint`'s own promise: viewers who hide
                    sensitive media "will see its thumbnails masked". The series page is separately
                    gated. `isViewerOwner` is the second half — the flag is the author's declaration
                    about their own work and must never be enforced against the author. */}
                {showCarousel && (
                    <SensitiveMediaGuard
                        isSensitive={isSensitive}
                        hideSensitiveMedia={hideSensitiveMedia}
                        sensitivePreference={sensitivePreference}
                        isViewerOwner={Boolean(displayedSeries.isViewerOwner)}
                        variant="card"
                        // The full-card link below is a sibling of this guard, so without this the masked scrim
                        // covers it and a blurred card cannot be opened without revealing first. Design §8.6.
                        overlayStacking="below-link-overlay"
                        badgeLabel={warningCopy.badgeLabel}
                        label={warningCopy.label}
                        helperText={warningCopy.helperText}
                        revealText={warningCopy.revealText}
                        preferencesRouteText={warningCopy.preferencesRouteText}
                    >
                    <div className="relative z-layer-raised-front pb-0">
                        {/* SV-78 ⑪ (round-33 D5): the SHARED feed-card media box, so a series card and a
                            discussion card in the same feed reserve the same well. FIXED mode only: series
                            covers store no dimensions (`SeriesDTOImpl`), so the `--aspect` modifier could
                            never apply. The hand-sized `h-56 sm:h-100` block it replaces left ~39% of a
                            phone-width well as backdrop filler. */}
                        <div className={`relative ${FEED_CARD_MEDIA_BOX_CLASS}`}>
                            {showLoadingSpinner && (
                                <div className="absolute inset-0 z-layer-raised-front flex items-center justify-center rounded-xl bg-background2/90 backdrop-blur-sm">
                                    <div className="flex flex-col items-center gap-3 rounded-xl border border-border/60 bg-background1/90 px-5 py-4 text-center shadow-sm">
                                        <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden={true}/>
                                        <span className="sr-only">{localizer("image.list.loading")}</span>
                                    </div>
                                </div>
                            )}

                            <DiscussionCardMediaCarousel
                                items={seriesMediaItems}
                                alt={displayTitle}
                                localizer={localizer}
                                onTap={() => {
                                    if (!seriesHref) return;
                                    navigateToUrl(seriesHref, {
                                        source: "series-card-image",
                                    });
                                }}
                                seavienImageSize={SMALL_IMAGE_SIZE}
                                className={showLoadingSpinner ? "mx-auto w-full invisible pointer-events-none" : "mx-auto w-full"}
                                slideHeightClassName={FEED_CARD_MEDIA_SLIDE_CLASS}
                                onRenderabilityChange={setImageRenderability}
                            />
                        </div>
                    </div>
                    </SensitiveMediaGuard>
                )}

                <CardHeader className="gap-1 px-3 py-3 sm:px-4 sm:py-4">
                    {/* `headingLevel={3}` — `EMPTY-STATES-83`, 2026-08-23. `CardTitle` renders a plain
                        `div` unless a host asks for the real element, and until this row exactly ONE
                        caller in the repo asked: `DiscussionCardDisplayer`, whose own comment calls
                        `3` "the ONE choice for every feed host". So `/my_dashboard/series` — a page
                        whose entire content is a list of series rows — exposed no heading at all
                        below the rail card's `h1`, and a screen-reader user had no way through the
                        list but to traverse every link and button inside every card.

                        `3` rather than a level of this card's own choosing, for the reason
                        `GROUP-4` recorded: the same card renders under several page structures, so
                        the level is picked once for the record tier — page `h1`, section `h2`,
                        record `h3` — or two pages showing the same card disagree about the tree. */}
                    <CardTitle headingLevel={3} className="text-lg font-semibold leading-tight text-foreground sm:text-xl">
                        <span className="inline-flex flex-wrap items-center gap-2">
                            <span>{displayTitle}</span>
                            {isSensitive && <SensitiveBadge className="py-0">{localizer("content-warning.badge.sensitive")}</SensitiveBadge>}
                        </span>
                    </CardTitle>

                    {/* text-neutral is a FILL token (gray-400) and measured 2.40:1 here — it does not even
                        reach the 3:1 large-text floor, and this is the attribution line naming who wrote
                        the series. text-neutral-text (gray-600) is the text half of that pair. The same
                        swap GroupCardDisplayer took; both tokens are declared on BOTH the wrapper and the
                        child because AuthorAttribution renders its own text nodes rather than inheriting. */}
                    {showAuthor ? (
                        <CardDescription className="text-xs text-neutral-text sm:text-sm">
                            <AuthorAttribution localizer={localizer}
                                name={displayOwnerUsername}
                                href={ownerProfilePath}
                                imageUrl={displayedSeries.ownerProfileImageUrl}
                                avatarSeed={displayedSeries.ownerUsername ?? displayOwnerUsername}
                                className="text-xs text-neutral-text sm:text-sm"
                            />
                        </CardDescription>
                    ) : null}
                </CardHeader>

                <CardContent className="px-3 py-0 pb-4 sm:px-4">
                    <div className="flex min-w-0 flex-col gap-4">
                        {/* `line-clamp-3` replaces `series-card-text`, a class name referenced here and
                            in GroupCardDisplayer and defined nowhere at all, so it clamped nothing while
                            reading like it did (ux-critique DISCOVERY-2). Three lines, matching the
                            group card, so a mixed directory row keeps one rhythm. */}
                        {/* `LANDING-69`: the element goes when there is nothing to put in it. An empty
                            `<p>` still occupies a 24px line box, so rendering it with an empty string
                            would trade a placeholder sentence for an unexplained gap rather than
                            collapsing the line. */}
                        {descriptionText ? (
                            <p className="line-clamp-3 wrap-anywhere text-sm leading-6 text-muted-foreground sm:text-[15px]">
                                {descriptionText}
                            </p>
                        ) : null}

                        {/* What is actually inside (ux-critique EMPTY-STATES-48). The row spent 400px of
                            593 on a cover and said nothing about its contents, so "which series should I
                            add this to" and "which have I left unfinished" both cost an open-and-go-back
                            per series. Three titles in the author's own sequence answer both at a glance.

                            The server sends these ONLY for series the viewer owns, so this is a
                            self-view block by construction and the public directory row is byte-identical
                            to what it was. Rendered as an ordered list because the sequence is the
                            information — `sort_order` is what the reading order follows.

                            ux-critique DASHBOARD-60 — the titles are a CAPTION of the contents, not a
                            list of destinations, and the colour is what has to say so. They shipped in
                            `text-text-main`, the primary text colour, which made them the most
                            tappable-looking thing on the card after the heading. They are not tappable:
                            the Card's last child is an `absolute inset-0` anchor to the SERIES, so a tap
                            on a named post silently opened the series and the member had to find the
                            post again in a second list. On a phone there is no hover to warn them first.

                            The row's other lever — give each title its own href — is NOT available:
                            `recentDiscussionTitles` is a `string[]` and carries no slug or public id per
                            entry, so linking them is a DTO + repository + generated-client change, not a
                            call-site one. `text-neutral-text` is the muted-but-accessible token the
                            EMPTY branch of this same block already uses, so the two states of one slot
                            now agree, and the block reads eyebrow then caption rather than eyebrow then
                            destinations. Size and leading are untouched: the defect is the colour, and
                            shrinking the text would trade one legibility problem for another. */}
                        {recentTitles.length > 0 ? (
                            <div className="flex min-w-0 flex-col gap-1" data-playwright-id="series-card-contents">
                                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                    {localizer("series.card.contents")}
                                </span>
                                <ol className="flex min-w-0 list-none flex-col gap-0.5">
                                    {recentTitles.map((title, index) => (
                                        <li
                                            key={`${index}-${title}`}
                                            className="truncate text-sm leading-6 text-neutral-text"
                                        >
                                            {title}
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        ) : isOwnedEmptySeries ? (
                            /* The zero branch of the block above (ux-critique `EMPTY-STATES-84`). An
                               empty series used to fall back to exactly the pre-EMPTY-STATES-48 card,
                               with the only cue that it is empty being a "0 posts" caption chip
                               identical in weight and colour to the "10 hours ago" chip beside it —
                               something the owner has to READ rather than SEE, on the page whose job is
                               to show which of their series are unfinished. Every series passes through
                               this state: it is the state a series is created in.

                               A statement, not a second CTA: "Add post" is already in the footer, which
                               is the row's recorded constraint.

                               `series.discussions.empty.title` is the series page's own empty-state
                               title for this exact condition, already carried in all 25 locales — reused
                               rather than minted (rule 11), which is also what keeps this batch out of
                               the catalogs (waves-10-and-11 charter S4). */
                            <p
                                className="text-sm leading-6 font-medium text-neutral-text"
                                data-playwright-id="series-card-contents-empty"
                            >
                                {localizer("series.discussions.empty.title")}
                            </p>
                        ) : null}

                        <CardMetadataBadges
                            className="gap-2"
                            badgeClassName="border-border/60 bg-muted/55"
                            items={[
                                ...(privateVisibilityLabel ? [{
                                    key: "visibility",
                                    icon: <Lock className="size-3.5" aria-hidden={true}/>,
                                    label: privateVisibilityLabel,
                                }] : []),
                                ...(createdAtLabel ? [{
                                    key: "createdAt",
                                    icon: <CalendarDays className="size-3.5" aria-hidden={true}/>,
                                    label: createdAtLabel,
                                }] : []),
                                /* `postCountLabel` opts into `showZero`: an empty series used to render
                                   no post pill at all, which made it byte-identical to a series whose
                                   count never arrived, on the owner's own list where the empty rows are
                                   exactly the ones that need action (ux-critique `EMPTY-STATES-28` (c)).
                                   `badge.series.discussions` is an ICU plural, so a known zero reads
                                   "0 posts". A count that is genuinely absent still drops the pill. */
                                ...(postCountLabel ? [{
                                    key: "discussions",
                                    icon: <MessageSquareText className="size-3.5" aria-hidden={true}/>,
                                    label: postCountLabel,
                                }] : []),
                                ...(getFriendlyCountBadgeLabel(localizer, "badge.series.followers", displayedSeries.followersCount) ? [{
                                    key: "followers",
                                    icon: <Users className="size-3.5" aria-hidden={true}/>,
                                    label: getFriendlyCountBadgeLabel(localizer, "badge.series.followers", displayedSeries.followersCount),
                                }] : []),
                            ]}
                        />
                    </div>
                </CardContent>

                {/* `mt-auto` pins the action row to the bottom of the card (`LANDING-26`). The landing
                    showcase passes `h-full` so every card grows to its tallest sibling, and nothing
                    inside took up the slack — up to ~116px (≈38% of the card) piled up as a void UNDER
                    the last control, which reads as "something failed to load here" on the one section
                    of the page showing real member work. `Card` is already `flex flex-col`, so this is
                    one property, and it is provably a no-op wherever the card sizes to its own content
                    (the live feed, the directory): there is no free space to distribute. */}
                <CardFooter className="relative z-layer-raised-front mt-auto flex flex-row flex-wrap items-center justify-end gap-2 px-3 pb-4 pt-0 sm:px-4">
                    <SeriesButtons
                        compactLabels={true}
                        seriesPublicId={displayedSeries.publicId ?? ""}
                        seriesSlug={displayedSeries.slug ?? ""}
                        initiallyOwner={Boolean(displayedSeries.isViewerOwner)}
                        initiallyFollowing={Boolean(displayedSeries.isViewerFollowing)}
                        seriesTitle={displayTitle}
                        seriesDescription={displayDescription}
                        seriesThumbnailUrl={displayedSeries.thumbnailUrl ?? ""}
                        mainLocale={(displayedSeries as SeriesDTO & { mainLocale?: string | null }).mainLocale ?? undefined}
                        privateAccess={Boolean(displayedSeries.privateAccess)}
                        bookmarksCollection={bookmarksCollection}
                        sensitive={isSensitive}
                        surface={surface}
                        localizer={localizer}
                    />
                </CardFooter>

            {seriesHref ? (
                // `focus-visible` treatment with a NEGATIVE outline offset (`EMPTY-STATES-41`). The card
                // root is `overflow-hidden`, so an outline at offset 0 paints outside this anchor's
                // border box and can be clipped away entirely — which is why the series card is the one
                // the row calls out by name. One string across all five card families.
                <a
                    href={seriesHref}
                    className="absolute inset-0 z-layer-base-front rounded-xl focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring"
                    aria-label={displayTitle}
                ></a>
            ) : null}
        </Card>
    );
}
